<template>
    <label :class="componentClasses">
        <textarea
            v-model.trim="message"
            :class="inputComponentClass"
            :placeholder="placeholderText"
            :disabled="chat.loading.value"
            @keydown.enter.prevent="onInput"
        />
        <span
            :class="submitComponentClass"
            @click="onInput"
            @keydown.enter="onInput"
        >
            <icon-send />
        </span>
    </label>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import IconSend from '@/assets/icons/send.svg?component';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';
    import Chat from '@/chat/Chat';

    const chat = Chat.getInstance();
    const { placeholderText } = useWidgetAppearance();
    const message = ref('');

    const canSendMessage = computed(() => {
        return message.value.length > 0 && !chat.loading.value;
    });

    const componentClasses = computed(() => ({
        'chat-input': true,
        'chat-input--disabled': message.value.length <= 0 && chat.loading.value,
        'chat-input--empty': message.value.length <= 0,
    }));

    const inputComponentClass = computed(() => ({
        'chat-input__input': true,
    }));

    const submitComponentClass = computed(() => ({
        'chat-input__submit': true,
    }));

    async function onInput() {
        if (!canSendMessage.value) {
            return;
        }

        const msg = message.value;

        message.value = '';

        await chat.sendMessage(
            msg,
        );
    }

    // prevent message from mutating when canSendMessage is false
    watch(() => message.value, () => {
        if (!canSendMessage.value) {
            message.value = '';
        }
    });
</script>

<style scoped lang="scss">
    .chat-input {
        display: grid;
        grid-template-columns: auto min-content;
        align-items: center;
        gap: 1rem;

        &__input {
            appearance: none;
            border: 0;
            outline: 0;
            background-color: transparent;
            resize: vertical;
            font-size: 16px;
            height: auto;
            min-height: 2rem;
            max-height: 120px;
            field-sizing: content;
        }

        &__submit {
            display: grid;
            border-radius: 10px;
            cursor: pointer;

            svg {
                width: 24px;
                height: 24px;
            }
        }
    }

    .app--flat {
        .chat-input {
            border-top: 1px solid #dadada;
            padding: 1rem;

            &__submit {
                padding: 1rem;
                background-color: var(--color-widget);
                color: var(--color-widget-text);
            }

            &__input {
                padding: 1rem;
            }

            &--disabled {
                & .chat-input__submit {
                    background-color: gray;
                    cursor: not-allowed;
                }
            }

            &--empty {
                & .chat-input__submit {
                    background-color: gray;
                    cursor: not-allowed;
                }
            }
        }
    }

    .app--modern {
        .chat-input {
            border-radius: 20px;
            border: solid 1px var(--color-widget);
            margin: 0 12px 12px 12px;
            padding: 1rem;

            &__submit {
                color: var(--color-widget);
            }

            &--disabled {
                border-color: gray;

                & .chat-input__submit {
                    & > svg {
                        color: gray;
                    }
                    cursor: not-allowed;
                }
            }

            &--empty {
                & .chat-input__submit {
                    & > svg {
                        color: gray;
                    }
                    cursor: not-allowed;
                }
            }
        }
    }
</style>
